import React from 'react'
import { 
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

interface ModerationSectionProps {
  title: string
  icon: React.ReactNode
  status?: 'approved' | 'pending' | 'rejected'
  hasModeration?: boolean
  children: React.ReactNode
  className?: string
}

export const ModerationSection: React.FC<ModerationSectionProps> = ({
  title,
  icon,
  status = 'approved',
  hasModeration = false,
  children,
  className = ''
}) => {
  const getStatusIcon = () => {
    if (!hasModeration) return null
    
    switch (status) {
      case 'approved':
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />
      case 'pending':
        return <ClockIcon className="w-4 h-4 text-yellow-600" />
      case 'rejected':
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />
      default:
        return null
    }
  }

  const getSectionStyle = () => {
    if (!hasModeration) return 'bg-white border-gray-200'
    
    switch (status) {
      case 'pending':
        return 'bg-gray-50 border-gray-200'
      case 'rejected':
        return 'bg-red-50 border-red-200'
      case 'approved':
        return 'bg-green-50 border-green-200'
      default:
        return 'bg-white border-gray-200'
    }
  }

  const getStatusBadge = () => {
    if (!hasModeration) return null
    
    switch (status) {
      case 'pending':
        return (
          <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-300">
            <ClockIcon className="w-3 h-3 mr-1" />
            На модерации
          </div>
        )
      case 'approved':
        return (
          <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Утверждено
          </div>
        )
      case 'rejected':
        return (
          <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
            Отклонено
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className={`rounded-lg border ${getSectionStyle()} ${className}`}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-base font-medium text-gray-900 flex items-center">
            {icon}
            <span className="ml-2">{title}</span>
            {getStatusIcon() && (
              <span className="ml-2">{getStatusIcon()}</span>
            )}
          </h4>
          {getStatusBadge()}
        </div>
        
        {children}
      </div>
    </div>
  )
}
